/**
 * Cart Quantity Dropdown Component
 *
 * A custom element that renders a "Qty: N ▾" button with a popover
 * for selecting quantity. Dispatches the same QuantitySelectorUpdateEvent
 * as the native quantity-selector-component so cart-items-component picks it up.
 */
class CartQuantityDropdownComponent extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #toggle = null;
  /** @type {HTMLElement | null} */
  #popover = null;
  /** @type {boolean} */
  #open = false;

  connectedCallback() {
    this.#toggle = this.querySelector('[ref="qtyToggle"]');
    this.#popover = this.querySelector('[ref="qtyPopover"]');

    if (this.#toggle) {
      this.#toggle.addEventListener('click', this.#handleToggle);
    }

    this.addEventListener('click', this.#handleOptionClick);
    document.addEventListener('click', this.#handleOutsideClick);
  }

  disconnectedCallback() {
    if (this.#toggle) {
      this.#toggle.removeEventListener('click', this.#handleToggle);
    }
    this.removeEventListener('click', this.#handleOptionClick);
    document.removeEventListener('click', this.#handleOutsideClick);
  }

  #handleToggle = (e) => {
    e.stopPropagation();
    this.#open = !this.#open;
    if (this.#popover) {
      this.#popover.hidden = !this.#open;
    }
  };

  #handleOptionClick = (e) => {
    const option = /** @type {HTMLElement} */ (e.target).closest('.cart-item-v2__qty-option');
    if (!option) return;

    const qty = parseInt(option.dataset.qty, 10);
    if (isNaN(qty)) return;

    const line = parseInt(this.dataset.line, 10);
    if (isNaN(line)) return;

    // Close popover
    this.#open = false;
    if (this.#popover) {
      this.#popover.hidden = true;
    }

    // Update the hidden quantity selector input to trigger the cart update
    const row = this.closest('tr');
    if (!row) return;

    const hiddenSelector = row.querySelector('cart-quantity-selector-component');
    if (!hiddenSelector) return;

    const input = hiddenSelector.querySelector('input[type="number"]');
    if (!input) return;

    // Set the new value
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value'
    ).set;
    nativeInputValueSetter.call(input, qty);

    // Dispatch the QuantitySelectorUpdateEvent
    const event = new CustomEvent('quantity-selector:update', {
      bubbles: true,
      detail: {
        quantity: qty,
        cartLine: line,
      },
    });
    input.dispatchEvent(event);

    // Update the button text
    if (this.#toggle) {
      const span = this.#toggle.querySelector('span:first-child');
      if (span) {
        const qtyLabel = span.textContent.replace(/\d+/, String(qty));
        span.textContent = qtyLabel;
      }
    }

    // Update selected state
    this.querySelectorAll('.cart-item-v2__qty-option').forEach((opt) => {
      opt.classList.toggle('is-selected', parseInt(opt.dataset.qty, 10) === qty);
    });
  };

  #handleOutsideClick = (e) => {
    if (this.#open && !this.contains(/** @type {Node} */ (e.target))) {
      this.#open = false;
      if (this.#popover) {
        this.#popover.hidden = true;
      }
    }
  };
}

if (!customElements.get('cart-quantity-dropdown-component')) {
  customElements.define('cart-quantity-dropdown-component', CartQuantityDropdownComponent);
}
